import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Button,
  Row,
  Col,
  Input,
  Select,
  Layout,
  Form,
  Upload,
  message,
  Flex,
  Tabs,
  Modal,
  Slider,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import { useDispatch, useSelector } from "react-redux";
import Cropper from "react-easy-crop";

import { getJwtPayload, getUserType, getBase64 } from "../../utils/appUtils";
import { VALIDATION_MESSAGES, API_BASE_URL } from "../../utils/constants";
import {
  updateProfileImage,
  selectProfileImage,
  fetchUserAddress,
  saveUserAddress,
  selectUserAddress,
  selectAddressSaveLoading,
  selectAddressSaveSuccess,
  selectAddressSaveError,
  resetAddressSaveSuccess,
} from "../../redux/userSlice";
import {
  fetchCountries,
  selectCountries,
  selectCountriesLoading,
} from "../../redux/locationSlice";
import PageLayout from "../../components/common/Layouts/PageLayout";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";

const { Content } = Layout;

const getImageExtensionFromDataUrl = (dataUrl) => {
  const match = typeof dataUrl === "string" && dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/i);
  if (!match) return null;

  const mime = match[1].toLowerCase();
  const mimeToExtension = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/bmp": "bmp",
    "image/svg+xml": "svg",
  };

  return mimeToExtension[mime] || mime.split("/")[1]?.split("+")[0] || null;
};

export default function ProfilePage() {
  // Form instance for programmatic control (pre-filling fields)
  const [form] = Form.useForm();
  const [addressForm] = Form.useForm();
  const activeRole = getUserType();
  const dispatch = useDispatch();
  const storedProfileImage = useSelector(selectProfileImage);

  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Address tab: local state for cascading state/city (avoid conflict with global location)
  const [profileStates, setProfileStates] = useState([]);
  const [profileCities, setProfileCities] = useState([]);
  const [profileStatesLoading, setProfileStatesLoading] = useState(false);
  const [profileCitiesLoading, setProfileCitiesLoading] = useState(false);

  const countries = useSelector(selectCountries);
  const countriesLoading = useSelector(selectCountriesLoading);
  const userAddress = useSelector(selectUserAddress);
  const addressSaveLoading = useSelector(selectAddressSaveLoading);
  const addressSaveSuccess = useSelector(selectAddressSaveSuccess);
  const addressSaveError = useSelector(selectAddressSaveError);
  const [imageUrl, setImageUrl] = useState(null);

  // Decode JWT token and pre-fill the Basic Info form on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const userPayload = getJwtPayload(token);

        // Pre-fill the form with token data
        form.setFieldsValue({
          fullName: `${userPayload?.first_name} ${userPayload?.last_name}` || '',
          emailID: userPayload.email || '',
          phoneNumber: userPayload.sub || '',
        });
      } catch (e) {
        console.error('Failed to decode auth token:', e);
      }
    }
  }, [form]);

  // Initialize avatar preview from stored profile image (if any)
  useEffect(() => {
    if (storedProfileImage) {
      setImageUrl(storedProfileImage);
    }
  }, [storedProfileImage]);

  // Fetch user address and countries when profile page mounts
  useEffect(() => {
    dispatch(fetchUserAddress());
    dispatch(fetchCountries());
  }, [dispatch]);

  // Pre-fill address form when userAddress is loaded; fetch states/cities for dropdowns
  useEffect(() => {
    if (!userAddress || !addressForm) return;
    const countryId = userAddress.country_id ?? userAddress.country?.id;
    const stateId = userAddress.state_id ?? userAddress.state?.id;
    const cityId = userAddress.city_id ?? userAddress.city?.id;
    addressForm.setFieldsValue({
      address_line1: userAddress.address_line1 ?? "",
      address_line2: userAddress.address_line2 ?? "",
      country: countryId ?? undefined,
      state: stateId ?? undefined,
      city: cityId ?? undefined,
      pincode: userAddress.pincode ?? "",
    });
    const countryCode = userAddress.country?.code;
    if (countryCode) {
      setProfileStatesLoading(true);
      axios
        .get(`${API_BASE_URL}/state-list/${countryCode}`, {
          headers: { Authorization: localStorage.getItem("authToken") },
        })
        .then((res) => {
          const data = res?.data?.data ?? res?.data;
          setProfileStates(Array.isArray(data) ? data : []);
        })
        .catch(() => setProfileStates([]))
        .finally(() => setProfileStatesLoading(false));
    }
    if (stateId) {
      setProfileCitiesLoading(true);
      axios
        .get(`${API_BASE_URL}/cities/${stateId}`, {
          headers: { Authorization: localStorage.getItem("authToken") },
        })
        .then((res) => {
          const data = res?.data?.data ?? res?.data;
          setProfileCities(Array.isArray(data) ? data : []);
        })
        .catch(() => setProfileCities([]))
        .finally(() => setProfileCitiesLoading(false));
    }
  }, [userAddress, addressForm]);

  // Address save success message
  useEffect(() => {
    if (addressSaveSuccess) {
      message.success("Address updated successfully.");
      dispatch(resetAddressSaveSuccess());
    }
  }, [addressSaveSuccess, dispatch]);

  // Address save error message
  useEffect(() => {
    if (addressSaveError) {
      message.error(addressSaveError);
    }
  }, [addressSaveError]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImg = useCallback((src, pixelCrop) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas context not available"));
          return;
        }
        ctx.drawImage(
          img,
          -pixelCrop.x,
          -pixelCrop.y,
          img.naturalWidth,
          img.naturalHeight
        );
        const base64Image = canvas.toDataURL("image/jpeg");
        resolve(base64Image);
      };
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }, []);

  const handleCropApply = useCallback(async () => {
    if (!cropSrc || !croppedAreaPixels) {
      setCropOpen(false);
      return;
    }

    try {
      const cropped = await getCroppedImg(cropSrc, croppedAreaPixels);
      setImageUrl(cropped);
      setCropOpen(false);
    } catch (e) {
      message.error("Failed to crop image. Please try again.");
    }
  }, [cropSrc, croppedAreaPixels, getCroppedImg]);

  const handleAvatarChange = async ({ file }) => {
    const origin = file.originFileObj || file;
    if (!origin) return;
    try {
      const src = await getBase64(origin);
      setCropSrc(src);
      setCropOpen(true);
    } catch (e) {
      message.error("Unable to read image file.");
    }
  };

  const handleUpdate = (values) => {
    if (!imageUrl) {
      message.error("Please upload and crop an image before updating.");
      return;
    }

    // Upload endpoint expects base64 data URL, not an existing S3/http URL.
    if (typeof imageUrl !== "string" || !imageUrl.startsWith("data:image/")) {
      message.error("Please select and crop a new image before updating.");
      return;
    }

    const fileExtension = getImageExtensionFromDataUrl(imageUrl);
    if (!fileExtension) {
      message.error("Invalid image format. Please try another file.");
      return;
    }

    dispatch(
      updateProfileImage({
        file: imageUrl,
        extension: fileExtension,
        previewUrl: imageUrl,
        fullName: values.fullName,
        phoneNumber: values.phoneNumber,
      })
    );

    message.success("Profile picture updated successfully.");
  };

  const handleCountryChange = (value) => {
    addressForm.setFieldsValue({ state: undefined, city: undefined });
    setProfileStates([]);
    setProfileCities([]);
    if (!value) return;
    const selectedCountry = countries.find((c) => c.id === value);
    if (selectedCountry?.code) {
      setProfileStatesLoading(true);
      axios
        .get(`${API_BASE_URL}/state-list/${selectedCountry.code}`, {
          headers: { Authorization: localStorage.getItem("authToken") },
        })
        .then((res) => {
          const data = res?.data?.data ?? res?.data;
          setProfileStates(Array.isArray(data) ? data : []);
        })
        .catch(() => setProfileStates([]))
        .finally(() => setProfileStatesLoading(false));
    }
  };

  const handleStateChange = (value) => {
    addressForm.setFieldsValue({ city: undefined });
    setProfileCities([]);
    if (!value) return;
    setProfileCitiesLoading(true);
    axios
      .get(`${API_BASE_URL}/cities/${value}`, {
        headers: { Authorization: localStorage.getItem("authToken") },
      })
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        setProfileCities(Array.isArray(data) ? data : []);
      })
      .catch(() => setProfileCities([]))
      .finally(() => setProfileCitiesLoading(false));
  };

  const handleAddressUpdate = (values) => {
    const token = localStorage.getItem("authToken");
    const payload = getJwtPayload(token);
    const userId = payload?.id;
    if (!userId) {
      message.error("Session expired. Please log in again.");
      return;
    }
    const body = {
      user_id: userId,
      country_id: values.country,
      state_id: values.state,
      city_id: values.city,
      address_line1: values.address_line1 || null,
      address_line2: values.address_line2 || null,
      pincode: values.pincode || null,
    };
    if (userAddress?.id) body.id = userAddress.id;
    dispatch(saveUserAddress(body));
  };

  const profileTabs = (
    <Tabs
      defaultActiveKey="all"
      className="eventTabs"
      type="card"
      items={[
        {
          key: "basicInfo",
          label: "Basic Info",
          children: (
            <div className="tabContentWrap">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={8}>
                  <Form form={form} layout="vertical" onFinish={handleUpdate}>
                    <Form.Item label="">
                      <Row gutter={[24, 0]} align="middle">
                        <Col xs={24} md={8}>
                          <Upload
                            listType="picture-circle"
                            showUploadList={false}
                            className="upload-circle-static"
                            beforeUpload={(file) => {
                              const isImage = file.type.startsWith("image/");
                              if (!isImage) {
                                message.error("Please upload an image file");
                                return Upload.LIST_IGNORE;
                              }
                              return false;
                            }}
                            onChange={handleAvatarChange}
                          >
                            <div className="upload-circle-inner">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt="avatar"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <PlusOutlined />
                              )}
                            </div>
                          </Upload>
                        </Col>
                      </Row>
                    </Form.Item>
                    <Form.Item
                      label=""
                      name="fullName"
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGES.NAME_REQUIRED,
                        },
                      ]}
                    >
                      <Input placeholder="Full Name" size="large" />
                    </Form.Item>
                    <Form.Item
                      label=""
                      name="emailID"
                      rules={[
                        {
                          required: true,
                          message: "Email ID is required",
                        },
                        {
                          type: "email",
                          message: VALIDATION_MESSAGES.EMAIL_INVALID,
                        },
                      ]}
                    >
                      <Input placeholder="Email ID" size="large" disabled />
                    </Form.Item>
                    <Form.Item
                      label=""
                      name="phoneNumber"
                      rules={[
                        {
                          required: true,
                          message:
                            VALIDATION_MESSAGES.PHONE_REQUIRED ||
                            "Phone Number is required",
                        },
                        {
                          pattern: /^[0-9]{10}$/,
                          message: VALIDATION_MESSAGES.PHONE_INVALID,
                        },
                      ]}
                    >
                      <Input placeholder="Phone Number" size="large" />
                    </Form.Item>

                    <Flex>
                      <Button
                        type="primary"
                        size="large"
                        className="btnGradient publishBtn"
                        htmlType="submit"
                      >
                        Update
                      </Button>
                    </Flex>
                  </Form>
                </Col>
              </Row>
            </div>
          ),
        },
        {
          key: "address",
          label: "Address",
          children: (
            <div className="tabContentWrap">
              <Row gutter={[0, 0]}>
                <Col xs={24} md={8}>
                  <Form
                    form={addressForm}
                    layout="vertical"
                    onFinish={handleAddressUpdate}
                  >
                    <Form.Item
                      label=""
                      name="address_line1"
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGES.ADDRESS_LINE1_REQUIRED,
                        },
                      ]}
                    >
                      <Input placeholder="Address line 1" size="large" />
                    </Form.Item>
                    <Form.Item label="" name="address_line2">
                      <Input placeholder="Address line 2" size="large" />
                    </Form.Item>
                    <Form.Item
                      label=""
                      name="country"
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGES.COUNTRY_REQUIRED,
                        },
                      ]}
                    >
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Country"
                        size="large"
                        loading={countriesLoading}
                        options={(countries || []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                        onChange={handleCountryChange}
                        showSearch
                        optionFilterProp="label"
                      />
                    </Form.Item>
                    <Row gutter={[24, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label=""
                          name="state"
                          rules={[
                            {
                              required: true,
                              message: VALIDATION_MESSAGES.STATE_REQUIRED,
                            },
                          ]}
                        >
                          <Select
                            style={{ width: "100%" }}
                            placeholder="State"
                            size="large"
                            loading={profileStatesLoading}
                            options={profileStates.map((s) => ({
                              value: s.id,
                              label: s.name,
                            }))}
                            onChange={handleStateChange}
                            showSearch
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label=""
                          name="city"
                          rules={[
                            {
                              required: true,
                              message: VALIDATION_MESSAGES.CITY_REQUIRED,
                            },
                          ]}
                        >
                          <Select
                            style={{ width: "100%" }}
                            placeholder="City"
                            size="large"
                            loading={profileCitiesLoading}
                            options={profileCities.map((c) => ({
                              value: c.id,
                              label: c.name,
                            }))}
                            showSearch
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label=""
                      name="pincode"
                      rules={[
                        {
                          required: true,
                          message: VALIDATION_MESSAGES.PINCODE_REQUIRED,
                        },
                      ]}
                    >
                      <Input placeholder="Pincode" size="large" maxLength={10} />
                    </Form.Item>

                    <Flex>
                      <Button
                        type="primary"
                        size="large"
                        className="btnGradient publishBtn"
                        htmlType="submit"
                        loading={addressSaveLoading}
                      >
                        Update
                      </Button>
                    </Flex>
                  </Form>
                </Col>
              </Row>
            </div>
          ),
        },
        {
          key: "kyc",
          label: "KYC",
          hidden: activeRole === "user",
          children: (
            <div className="tabContentWrap">
              <Row gutter={[0, 0]}>
                <Col xs={24} md={8}>
                  <Form layout="vertical">
                    <Form.Item
                      label=""
                      name="aadharNumber"
                      rules={[
                        {
                          required: true,
                          //message: 'Aadhar Number is required',
                        },
                      ]}
                    >
                      <Input placeholder="Aadhar Number" size="large" />
                    </Form.Item>
                    <Form.Item
                      label=""
                      name="otp"
                      rules={[
                        {
                          required: true,
                          //message: 'Enter OTP is required',
                        },
                      ]}
                    >
                      <Input placeholder="Enter OTP" size="large" />
                    </Form.Item>

                    <Flex>
                      <Button
                        type="primary"
                        size="large"
                        className="btnGradient publishBtn"
                        htmlType="submit"
                        disabled
                      >
                        Update
                      </Button>
                    </Flex>
                  </Form>
                </Col>
              </Row>
            </div>
          ),
        },
      ].filter(tab => !tab.hidden)}
    />
  );

  return (
    <>
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
          and city-specific visibility for organizers and users." 
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      {activeRole === "organizer" ? (
        <PageLayout title="My Profile">
          <Content>{profileTabs}</Content>
        </PageLayout>
      ) : (
        <>
          <Header isHome={false} />
          <Content className="topheaderLayout">{profileTabs}</Content>
          {/* FOOTER */}
          <Footer />
        </>
      )}

      <Modal
        title="Crop Photo"
        open={cropOpen}
        onOk={handleCropApply}
        onCancel={() => setCropOpen(false)}
        okText="Apply"
        destroyOnClose
      >
        <div style={{ position: "relative", width: "100%", height: 300 }}>
          {cropSrc && (
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <span style={{ display: "block", marginBottom: 8 }}>Zoom</span>
          <Slider
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={setZoom}
          />
        </div>
      </Modal>
    </>
  );
}
